<template>
  <div class="pa-4">
    <h2>🔐 Matriz RBAC (Routes ↔ Roles/Permissions)</h2>
    <p class="text-body-2 mb-4">Fuente: router.getRoutes(). Exporta para documentación o revisión.</p>

    <div class="d-flex gap-2 mb-4">
      <v-btn color="primary" @click="exportJson">Exportar JSON</v-btn>
      <v-btn color="secondary" @click="exportCsv">Exportar CSV</v-btn>
      <v-spacer />
      <v-text-field v-model="q" density="compact" label="Filtrar" prepend-inner-icon="mdi-magnify" hide-details class="w-33" />
    </div>

    <v-table density="comfortable">
      <thead>
        <tr>
          <th>Ruta</th>
          <th>Nombre</th>
          <th>RequiresAuth</th>
          <th>Roles</th>
          <th>Permissions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in filtered" :key="row.path + ':' + row.name">
          <td><code>{{ row.path }}</code></td>
          <td>{{ row.name }}</td>
          <td>{{ row.requiresAuth ? 'sí' : 'no' }}</td>
          <td>
            <span v-if="row.roles?.length">{{ row.roles.join(', ') }}</span>
            <span v-else class="text-disabled">—</span>
          </td>
          <td>
            <span v-if="row.permissions?.length">{{ row.permissions.join(', ') }}</span>
            <span v-else class="text-disabled">—</span>
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script setup lang="ts">
import router from '@/router'
import { computed, ref } from 'vue'

interface RbacRow {
  path: string
  name: string
  requiresAuth: boolean
  roles?: string[]
  permissions?: string[]
}

const q = ref('')

const rows = computed<RbacRow[]>(() => {
  return router.getRoutes()
    .map(r => ({
      path: r.path,
      name: String(r.name ?? ''),
      requiresAuth: Boolean(r.meta?.requiresAuth),
      roles: Array.isArray(r.meta?.roles) ? (r.meta!.roles as string[]) : undefined,
      permissions: Array.isArray(r.meta?.permissions) ? (r.meta!.permissions as string[]) : undefined,
    }))
    .sort((a, b) => a.path.localeCompare(b.path))
})

const filtered = computed(() => {
  const term = q.value.trim().toLowerCase()
  if (!term) return rows.value
  return rows.value.filter(r =>
    r.path.toLowerCase().includes(term) ||
    r.name.toLowerCase().includes(term) ||
    (r.roles?.join(',').toLowerCase().includes(term) ?? false) ||
    (r.permissions?.join(',').toLowerCase().includes(term) ?? false)
  )
})

function exportJson() {
  const blob = new Blob([JSON.stringify(rows.value, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'rbac-matrix.json'
  a.click()
  URL.revokeObjectURL(url)
}

function exportCsv() {
  const header = ['path', 'name', 'requiresAuth', 'roles', 'permissions']
  const lines = [header.join(',')]
  for (const r of rows.value) {
    const csvRow = [
      JSON.stringify(r.path),
      JSON.stringify(r.name),
      r.requiresAuth ? 'true' : 'false',
      JSON.stringify(r.roles?.join('|') ?? ''),
      JSON.stringify(r.permissions?.join('|') ?? ''),
    ]
    lines.push(csvRow.join(','))
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'rbac-matrix.csv'
  a.click()
  URL.revokeObjectURL(url)
}
</script>

<style scoped>
.gap-2 { gap: .5rem; }
.w-33 { width: 33%; }
</style>
